import { pgEnum } from 'drizzle-orm/pg-core'

// Code execution status
export const executionStatusEnum = pgEnum('ExecutionStatus', [
  'pending',
  'running',
  'completed',
  'failed',
])

// Coaching client status
export const coachingClientStatusEnum = pgEnum('CoachingClientStatus', [
  'active',
  'paused',
  'archived',
])
export type CoachingClientStatus =
  (typeof coachingClientStatusEnum.enumValues)[number]

// Coaching homework status
export const coachingHomeworkStatusEnum = pgEnum('CoachingHomeworkStatus', [
  'active',
  'removed',
  'completed',
])
export type CoachingHomeworkStatus =
  (typeof coachingHomeworkStatusEnum.enumValues)[number]

// A time-bounded coaching program's lifecycle.
export const coachingEngagementStatusEnum = pgEnum('CoachingEngagementStatus', [
  'active',
  'paused',
  'completed',
  'cancelled',
])
export type CoachingEngagementStatus =
  (typeof coachingEngagementStatusEnum.enumValues)[number]

// Coach-configurable feedback intensity.
export const feedbackLevelEnum = pgEnum('FeedbackLevel', [
  'low',
  'normal',
  'high',
])

// Coach-configurable pace target used to derive coding time estimates.
// Values mirror COACHING_PACES in lib/coaching/estimate-minutes.ts (schema
// files can't import app modules) — keep the two lists in sync.
export const coachingPaceEnum = pgEnum('CoachingPace', [
  'fast',
  'average',
  'slow',
])

// FK-safe catalog kinds projected through LearningItem.
export const learningItemKindEnum = pgEnum('LearningItemKind', [
  'problem',
  'coding_exercise',
  'system_design',
  'task',
  'resource',
  'source_content',
  'behavioral',
])

// External workspace provider. Delivery identity remains provider-scoped
// through its owning connection rather than duplicating provider on delivery.
export const externalWorkspaceProviderEnum = pgEnum(
  'ExternalWorkspaceProvider',
  ['notion'],
)

// External destination validation and enablement lifecycle.
export const externalWorkspaceConnectionStatusEnum = pgEnum(
  'ExternalWorkspaceConnectionStatus',
  ['active', 'invalid', 'disabled'],
)

// Durable external-delivery queue and reconciliation lifecycle.
export const assignmentExternalDeliveryStateEnum = pgEnum(
  'AssignmentExternalDeliveryState',
  [
    'queued',
    'creating',
    'succeeded',
    'failed',
    'unknown',
    'reconciled',
    'archived_without_push',
  ],
)

// Delivery-time template selection contract.
export const deliveryTemplatePolicyEnum = pgEnum('DeliveryTemplatePolicy', [
  'auto',
  'none',
  'explicit',
])

// Final classification for one external-delivery attempt.
export const assignmentDeliveryAttemptOutcomeEnum = pgEnum(
  'AssignmentDeliveryAttemptOutcome',
  ['succeeded', 'failed', 'unknown'],
)

// Per-client dashboard presentation selector; canonical writes are universal.
export const coachingDashboardExperienceEnum = pgEnum(
  'CoachingDashboardExperience',
  ['classic', 'v2'],
)

// Coaching plan publication lifecycle.
export const weekStatusEnum = pgEnum('WeekStatus', [
  'draft',
  'published',
  'closed',
])
export type WeekStatus = (typeof weekStatusEnum.enumValues)[number]

// Who published a plan week (LG-1003). `coach` is a person pressing publish and
// always carries a `publishedByUserId`; `sprint_start` is the sprint-start
// sweep releasing a locked client's drafted week at the Sprint's Monday, and is
// the ONLY source allowed to leave that column null --
// `CoachingPlanWeek_publication_fields_check` states that biconditional in SQL.
export const publishSourceEnum = pgEnum('PublishSource', [
  'coach',
  'sprint_start',
])
export type PublishSource = (typeof publishSourceEnum.enumValues)[number]

// A plan day's scheduling role.
export const dayKindEnum = pgEnum('DayKind', [
  'normal',
  'blocked',
  'interview',
  'availability',
])
export type DayKind = (typeof dayKindEnum.enumValues)[number]

// Controls whether an assignment is immediately client-visible.
export const presentationModeEnum = pgEnum('PresentationMode', [
  'normal',
  'ai_mock_hidden',
])

// Determines whether completion is direct or requires submission review.
export const completionModeEnum = pgEnum('CompletionMode', [
  'manual',
  'submission_review',
])

// Assignment workflow lifecycle.
export const assignmentStatusEnum = pgEnum('AssignmentStatus', [
  'planned',
  'in_progress',
  'submitted',
  'completed',
  'cancelled',
])
export type AssignmentStatus = (typeof assignmentStatusEnum.enumValues)[number]

// Immutable coaching artifact kind submitted for review.
//
// `manual` (LG-905 D3) is the odd one out: it carries no artifact, because the
// client ticked a card done rather than handing work in. It exists so that a
// ticked card still has a submission for a review to hang off — without one a
// coach has nothing to comment on. The name of the enum is left alone; renaming
// it to fit the fourth value is churn for its own ticket.
export const submissionKindEnum = pgEnum('SubmissionKind', [
  'coding',
  'system_design',
  'behavioral',
  'manual',
])
export type SubmissionKind = (typeof submissionKindEnum.enumValues)[number]

// Coaching review queue lifecycle.
export const reviewStatusEnum = pgEnum('ReviewStatus', [
  'awaiting',
  'in_review',
  'reviewed',
  'reopened',
  'superseded',
])
export type ReviewStatus = (typeof reviewStatusEnum.enumValues)[number]

// Origin of a review comment.
export const reviewCommentSourceEnum = pgEnum('ReviewCommentSource', [
  'ai',
  'coach',
])
export type ReviewCommentSource =
  (typeof reviewCommentSourceEnum.enumValues)[number]

// Coach-visible feedback taxonomy.
export const reviewCommentKindEnum = pgEnum('ReviewCommentKind', [
  'praise',
  'critique',
  'suggestion',
])
export type ReviewCommentKind =
  (typeof reviewCommentKindEnum.enumValues)[number]

// Review comment publication lifecycle.
export const reviewCommentStatusEnum = pgEnum('ReviewCommentStatus', [
  'pending',
  'approved',
  'posted',
  'dismissed',
])
export type ReviewCommentStatus =
  (typeof reviewCommentStatusEnum.enumValues)[number]

// Immutable revision publication lifecycle.
export const reviewCommentRevisionStatusEnum = pgEnum(
  'ReviewCommentRevisionStatus',
  ['draft', 'published', 'superseded'],
)

// Origin of a review comment revision.
export const reviewCommentRevisionSourceEnum = pgEnum(
  'ReviewCommentRevisionSource',
  ['coach_edit', 'ai_rewrite'],
)

// Typed review attachment subject. Three of the four name a durable row the
// attachment points at; `snapshot` is the one for a subject that has no row of
// its own and must be frozen into the attachment instead (LG-644).
export const reviewAttachmentKindEnum = pgEnum('ReviewAttachmentKind', [
  'learning_item',
  'assignment',
  'snapshot',
  'approach',
])

export type ReviewAttachmentKind =
  (typeof reviewAttachmentKindEnum.enumValues)[number]

// Provenance class for review pacing benchmarks.
export const reviewBenchmarkSourceKindEnum = pgEnum(
  'ReviewBenchmarkSourceKind',
  ['coach_authored', 'empirical'],
)

// Versioned company behavior-rubric lifecycle.
export const companyBehaviorRubricStatusEnum = pgEnum(
  'CompanyBehaviorRubricStatus',
  ['draft', 'active', 'retired'],
)

// Story Fit assessment producer.
export const storyFitAssessmentSourceEnum = pgEnum('StoryFitAssessmentSource', [
  'coach',
  'ai_assisted',
])

// Story Fit assessment result.
export const storyFitSignalEnum = pgEnum('StoryFitSignal', [
  'positive',
  'mixed',
  'negative',
  'insufficient_evidence',
])

// Recommendation decision lifecycle.
export const coachingRecommendationStatusEnum = pgEnum(
  'CoachingRecommendationStatus',
  ['suggested', 'accepted', 'dismissed', 'assigned', 'expired'],
)

export const clientInterviewSchedulingKindEnum = pgEnum(
  'ClientInterviewSchedulingKind',
  ['locked', 'availability', 'tbd'],
)

// Problem relationship types (inter-problem connections)
export const problemRelationshipTypeEnum = pgEnum('ProblemRelationshipType', [
  'prerequisite',
  'builds_on',
  'harder_than',
])
export type ProblemRelationshipType =
  (typeof problemRelationshipTypeEnum.enumValues)[number]

// Problem link kind: 'variant' = modified version of a canonical problem,
// 'duplicate' = the same question published by a different source
export const problemLinkKindEnum = pgEnum('ProblemLinkKind', [
  'variant',
  'duplicate',
])

// Relationship strength levels
export const relationshipStrengthEnum = pgEnum('RelationshipStrength', [
  'required',
  'recommended',
  'optional',
])
export type RelationshipStrength =
  (typeof relationshipStrengthEnum.enumValues)[number]

// Problem publishing status
export const problemStatusEnum = pgEnum('ProblemStatus', ['draft', 'published'])
export type ProblemStatus = (typeof problemStatusEnum.enumValues)[number]

// Problem visibility levels
export const problemVisibilityEnum = pgEnum('ProblemVisibility', [
  'public',
  'client_only',
  'admin_only',
])
export type ProblemVisibility =
  (typeof problemVisibilityEnum.enumValues)[number]

// Approach solution type classification
export const solutionTypeEnum = pgEnum('solutionType', [
  'brute_force',
  'acceptable',
  'optimal',
])

// Editorial format type
export const editorialFormatEnum = pgEnum('EditorialFormat', ['new', 'legacy'])

// Study plan type (coding vs system design)
export const studyPlanTypeEnum = pgEnum('StudyPlanType', [
  'coding',
  'system_design',
])

// Flashcard difficulty rating for postmortem self-assessment
export const flashcardDifficultyEnum = pgEnum('FlashcardDifficulty', [
  'again',
  'struggled',
  'intermediate_effort',
  'effortless',
])

// Interview experience outcome
export const outcomeEnum = pgEnum('Outcome', [
  'offer',
  'rejected',
  'pending',
  'unknown',
])

// Interview question report type
export const questionTypeEnum = pgEnum('QuestionType', [
  'coding',
  'system_design',
  'behavioral',
  'other',
])

// Perceived difficulty for interview questions
export const perceivedDifficultyEnum = pgEnum('PerceivedDifficulty', [
  'easy',
  'medium',
  'hard',
])

// Recommendation engine: primary/secondary subtopic weighting per problem
export const problemSubtopicWeightEnum = pgEnum('ProblemSubtopicWeight', [
  'primary',
  'secondary',
])

// Taxonomy overlay: audience tracks are branches, not a ladder. General
// interview prep is the trunk; tracks fork from it with partial overlap. A
// subtopic's audiences live in SubtopicAudience rows: no rows = baseline
// (everyone preps it); rows = specialized, front-and-center in the listed
// tracks and greyed-out (never hidden) elsewhere. `ready-for-anything` is the
// apex: super-advanced material in nobody's core track. `startups` and
// `fintech` are defined but uncurated so far.
export const audienceTrackEnum = pgEnum('AudienceTrack', [
  'big-tech',
  'ai-startups',
  'startups',
  'fintech',
  'ready-for-anything',
])
export type AudienceTrack = (typeof audienceTrackEnum.enumValues)[number]

/**
 * Whether a subtopic is core (non-greyed) material for a learner's track.
 * `audiences` is the subtopic's audience values — the `audience` column of
 * its SubtopicAudience rows (empty = baseline, core everywhere); the
 * ready-for-anything track sees everything. A combined multi-track view is
 * the union of this check across the chosen tracks.
 */
export function subtopicInTrack(
  audiences: readonly AudienceTrack[],
  track: AudienceTrack,
): boolean {
  return (
    audiences.length === 0 ||
    track === 'ready-for-anything' ||
    audiences.includes(track)
  )
}

// Taxonomy modifiers: value families. Exclusivity is an app-layer rule;
// EXCLUSIVE_MODIFIER_FAMILIES below is the single authority for which
// families allow at most one value per approach.
export const modifierFamilyEnum = pgEnum('ModifierFamily', [
  'dp-style',
  'stack-model',
  'space-trick',
  'standalone',
])
export type ModifierFamily = (typeof modifierFamilyEnum.enumValues)[number]

/** Families allowing at most one value per approach (enforced in app code). */
export const EXCLUSIVE_MODIFIER_FAMILIES: ReadonlySet<ModifierFamily> = new Set(
  ['dp-style', 'stack-model'],
)

// Recommendation engine: distinguishes the first time an event was processed
// (`original`) from a re-derivation of that same event triggered by a major
// setting change (`replay`). Replays MUST source from original events only —
// never from a prior replay. Chaining replay onto replay would compound each
// re-derivation's rounding into numbers no raw event supports; forbidding it
// is what keeps every displayed figure traceable to something that happened.
export const calculationLogEntryKindEnum = pgEnum('CalculationLogEntryKind', [
  'original',
  'replay',
])

// Recommendation engine: manual processing mode selected from pending changes.
export const processingTypeEnum = pgEnum('ProcessingType', [
  'process',
  'reprocess',
  'full_reprocess',
  'none',
])

// Recommendation engine / coaching adjustment reason taxonomy
export const reasonCodeEnum = pgEnum('ReasonCode', [
  'student_attempt_easy',
  'student_attempt_good',
  'student_attempt_hard',
  'student_attempt_again',
  'threshold_change',
  'rollup_weight_change',
  'preset_applied',
  'target_company_change',
  'baseline_level_change',
  'topic_inclusion_change',
  'subtopic_enable_toggle',
  'weekly_commitment_change',
  'target_date_change',
  'attempt_reviewed',
  'coach_seed',
  'coach_seed_override',
  'fsrs_recompute',
  'decay_catchup',
  'coach_revert',
  'coach_reapply',
  'coach_revert_to_snapshot',
  'reprocess_cascade',
  'processing_run_requested',
  'settings_snapshot_captured',
  'baseline_epoch_change',
  'graph_json_saved',
])

// Reference type for interview question reports
export const referenceTypeEnum = pgEnum('ReferenceType', [
  'exact',
  'variation',
  'novel',
])

// Reason a problem session closed
export const sessionCloseReasonEnum = pgEnum('SessionCloseReason', [
  'solved',
  'expired',
])
export type SessionCloseReason =
  (typeof sessionCloseReasonEnum.enumValues)[number]

/**
 * Funnel stage for the audition onboarding flow (LG-118). Full life cycle is
 * defined up front so later slices (payments, booking, admin) never migrate
 * this enum; slice 1 only ever writes the first two values.
 */
export const auditionStageEnum = pgEnum('AuditionStage', [
  'wizard_in_progress',
  'audition_completed',
  'paid_pending_call',
  'vibe_call_booked',
  'payment_unlocked',
  'client',
  'rejected',
])

/**
 * What kind of coding surface a Problem needs (LG-55).
 *
 * ONLY content that changes the coding surface earns a value here: SQL needs a
 * schema browser, seed data, and a result grid; shell needs a terminal.
 * Descriptors that keep today's editor — concurrency, interactive, low-level
 * design — are problem tags or test-config concerns, not types. The test for
 * adding a value here: does it change what the editor must render? If not, it
 * is a tag.
 */
export const problemTypeEnum = pgEnum('ProblemType', [
  'algorithms',
  'sql',
  'shell',
])
export type ProblemType = (typeof problemTypeEnum.enumValues)[number]

// Audition applicant's pricing/routing lane (LG-118).
export const auditionLaneEnum = pgEnum('AuditionLane', [
  'fast',
  'consideration',
])
export type AuditionLane = (typeof auditionLaneEnum.enumValues)[number]

// Admin-proposed vibe-call slot batch lifecycle (LG-118).
//
// `no_show` is distinct from `cancelled` (LG-414): cancelled means the offer
// was withdrawn (superseded, or the applicant was rejected), while no_show
// means a real booked call that the applicant did not attend. The admin
// timeline and the queue's Call facet both need to tell those apart — folding
// a no-show into `cancelled` made an absent applicant look like an
// administrative withdrawal.
export const callProposalStatusEnum = pgEnum('CallProposalStatus', [
  'proposed',
  'selected',
  'held',
  'no_show',
  'cancelled',
])
export type CallProposalStatus =
  (typeof callProposalStatusEnum.enumValues)[number]

// Google Calendar event creation lifecycle for a selected call slot (LG-118).
export const calendarBookingStatusEnum = pgEnum('CalendarBookingStatus', [
  'none',
  'pending',
  'created',
  'failed',
])
export type CalendarBookingStatus =
  (typeof calendarBookingStatusEnum.enumValues)[number]

/**
 * Lifecycle of a call booked through cal.com (LG-902).
 *
 * Deliberately the same outcome words as `CallProposalStatus` — `held`,
 * `no_show`, `cancelled` — because the queue's Call facet and the admin
 * timeline read both tables and must not learn two vocabularies for one fact.
 * `proposed`/`selected` have no counterpart here: cal.com only ever tells us
 * about a booking that already exists.
 *
 * `no_show` means the APPLICANT did not attend. A host who missed their own
 * call is `hostNoShow` on the row, not a status — folding the two together
 * would put our own miss on the applicant's record and read as a reason to
 * reject them.
 */
export const callBookingStatusEnum = pgEnum('CallBookingStatus', [
  // Awaiting OUR decision. Only reachable while the cal.com event type
  // requires confirmation: the slot is held but nobody has been let in yet.
  'pending',
  'booked',
  // Refused at the door, by us or by a host clicking reject in cal.com. A
  // distinct word from 'cancelled' on purpose: a cancellation is a call that
  // was real and was called off, a decline is a call that never existed.
  'declined',
  'cancelled',
  'held',
  'no_show',
])
export type CallBookingStatus =
  (typeof callBookingStatusEnum.enumValues)[number]

/**
 * How a cal.com booking was tied back to an applicant (LG-902).
 *
 * Stored rather than derived because the two are not equally trustworthy: an
 * id carried through the booking link is the applicant we invited, while an
 * email match is an inference that can attach a call to the wrong person
 * (shared inbox, a partner booking on someone's behalf). An admin looking at
 * a surprising booking needs to see which one it was.
 */
export const callBookingMatchEnum = pgEnum('CallBookingMatch', [
  'submission_id',
  'attendee_email',
])
export type CallBookingMatch = (typeof callBookingMatchEnum.enumValues)[number]

// Reason for a CreditLedger entry (LG-118).
export const creditLedgerReasonEnum = pgEnum('CreditLedgerReason', [
  'purchase',
  'admin_adjust',
  'refund_clawback',
  'consume',
])
export type CreditLedgerReason =
  (typeof creditLedgerReasonEnum.enumValues)[number]

// Reason an applicant is placed in cooldown (LG-118).
export const cooldownReasonEnum = pgEnum('CooldownReason', [
  'rejected',
  'refunded',
])
export type CooldownReason = (typeof cooldownReasonEnum.enumValues)[number]

// A company-owned Sprint's admin-controlled lifecycle (LG-999). No "cancelled"
// state — a Sprint is soft-deleted instead; suspended charges nobody and
// publishes nothing but the row (and its history) stays.
export const sprintStatusEnum = pgEnum('SprintStatus', [
  'scheduled',
  'suspended',
])
export type SprintStatus = (typeof sprintStatusEnum.enumValues)[number]

// A per-engagement Sprint membership's lifecycle (LG-999). `completed` is
// deliberately absent -- it's derived from `locked` + the Sprint's `endsAt`,
// never stored (spec: "Implementation Decisions" / SprintParticipation).
export const sprintParticipationStatusEnum = pgEnum(
  'SprintParticipationStatus',
  ['upcoming', 'skipped', 'locked', 'unfunded'],
)
export type SprintParticipationStatus =
  (typeof sprintParticipationStatusEnum.enumValues)[number]

// Which flavour of system-design interview a problem is written for (LG-930).
//
// Set per problem, not discoverable on the whiteboard page, and it changes both
// the interviewer's prompting and the grading. The two values are the pair the
// LG-922 grill settled on; the enum is deliberately open — more are expected,
// and adding one is an `ALTER TYPE ... ADD VALUE` migration.
export const systemDesignInterviewTypeEnum = pgEnum(
  'SystemDesignInterviewType',
  ['product', 'infrastructure'],
)
export type SystemDesignInterviewType =
  (typeof systemDesignInterviewTypeEnum.enumValues)[number]

// The scale a system-design problem is posed at (LG-930).
//
// Same story as `SystemDesignInterviewType`: per problem, prompting- and
// grading-affecting, and explicitly not believed to be sufficient at two
// values (Mike, LG-922 R2) — expect this list to grow.
export const systemDesignCompanyContextEnum = pgEnum(
  'SystemDesignCompanyContext',
  ['startup', 'large_scale'],
)
export type SystemDesignCompanyContext =
  (typeof systemDesignCompanyContextEnum.enumValues)[number]

// Which domain's static postmortem question set a bank row belongs to (LG-930).
//
// The postmortem gate applies to every AI interview type, and each domain gets
// its own static question set out of the one bank. `coding` is the whole
// existing corpus, which is why it is the column default.
export const postMortemDomainEnum = pgEnum('PostMortemDomain', [
  'coding',
  'system_design',
])
export type PostMortemDomain = (typeof postMortemDomainEnum.enumValues)[number]

// Where the grader is allowed to look when judging one assertion (LG-931).
//
// Per assertion, not per problem: "the diagram shows a cache" is answerable
// from the board alone, while "they justified the cache eviction policy" is
// not. Narrowing the window is what stops the grader crediting a claim the
// candidate only said out loud on a check meant to test the drawing.
export const systemDesignAssertionGradedFromEnum = pgEnum(
  'SystemDesignAssertionGradedFrom',
  ['board', 'board_and_conversation'],
)
export type SystemDesignAssertionGradedFrom =
  (typeof systemDesignAssertionGradedFromEnum.enumValues)[number]

// What caused a run of the checks (LG-931, amended from the LG-954 grill).
//
// Mid-session runs are the ones the learner asks for while still working; the
// final run is the one the results screen reads. Both are kept — the run
// history is the point of `CheckRun` existing at all, and deliberately has no
// cap here: the 3-run budget is a later product feature, not a schema rule.
export const checkRunTriggerEnum = pgEnum('CheckRunTrigger', [
  'mid_session',
  'final',
])
export type CheckRunTrigger = (typeof checkRunTriggerEnum.enumValues)[number]

// How one assertion came out on one run (LG-931).
//
// `partial_credit` is a fixed half point rather than a free-form score, which
// is what makes a total like "3.5/6" meaningful without storing weights.
// `not_attempted` is distinct from `fail` on purpose: never reaching a topic
// and getting it wrong are different feedback, and collapsing them would make
// an unfinished session look like a failed one.
export const checkResultStateEnum = pgEnum('CheckResultState', [
  'pass',
  'partial_credit',
  'fail',
  'not_attempted',
])
export type CheckResultState = (typeof checkResultStateEnum.enumValues)[number]
// Lifecycle of one whiteboard attempt (LG-932).
//
// Linear, never skipping: a session ends, the postmortem gate holds results
// back until the self-reflection is submitted (LG-922 decision 6), and only
// then are results readable. `abandoned` is the terminal state for a session
// that was never ended properly — a lazy-close on next open, mirroring
// `ProblemSession`'s `expired` close reason — and is reachable only from
// `in_progress`.
export const whiteboardSessionStateEnum = pgEnum('WhiteboardSessionState', [
  'in_progress',
  'ended',
  'postmortem_pending',
  'results_ready',
  'abandoned',
])
export type WhiteboardSessionState =
  (typeof whiteboardSessionStateEnum.enumValues)[number]

// What one appended whiteboard op did to the board (LG-932).
//
// The three verbs the stripped form can replay. `delete` carries the element
// id with no body; `add` and `update` carry the element's full stripped line,
// so replaying an op never needs the op before it.
export const whiteboardOpKindEnum = pgEnum('WhiteboardOpKind', [
  'add',
  'update',
  'delete',
])
export type WhiteboardOpKind = (typeof whiteboardOpKindEnum.enumValues)[number]
